import React, { useEffect, useState, useRef } from "react";
import { X, Copy, Clock, Minimize2, Maximize2, Phone } from "lucide-react";
import { toast } from "sonner";

/**
 * Embedded in-app call panel — backed by Daily.co.
 *
 * Why a plain <iframe> and not the @daily-co/daily-js SDK?
 *   Daily.co supports direct iframe embedding of public room URLs. The SDK
 *   adds programmatic controls (mute / leave hooks / transcripts) but for our
 *   use case the room's built-in UI is enough. Going pure-iframe sidesteps the
 *   whole class of React-StrictMode lifecycle bugs that plagued the SDK route
 *   (duplicate instances, synthetic left-meeting on destroy, blank-iframe
 *   sizing races, etc.).
 *
 * Layout modes:
 *   - default     : floating top-right card (560×680)
 *   - fullscreen  : near-edge fullscreen
 *   - minimized   : compact bottom-right pill (iframe stays MOUNTED but is
 *                   hidden via CSS; unmounting it drops the call session).
 *
 * Closing:
 *   - X in our header → calls onClose()
 *   - User clicks Daily's own "Leave" button → Daily redirects the iframe to a
 *     thank-you screen but the iframe stays inside our panel. The X button is
 *     always available so the user can dismiss us.
 */
export default function EmbeddedCallPanel({
  roomUrl,
  displayName,
  audioOnly,
  onClose,
  durationLimitMin = 60,
}) {
  const [remaining, setRemaining] = useState(durationLimitMin * 60);
  const [mode, setMode] = useState("default"); // 'default' | 'fullscreen' | 'minimized'

  useEffect(() => {
    const t = setInterval(() => setRemaining((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(t);
  }, []);

  // Pre-fill the display name + skip prejoin via Daily URL params so the user
  // isn't asked for a name every time. `t=…` is Daily's display-name param.
  const builtUrl = (() => {
    if (!roomUrl) return null;
    try {
      const u = new URL(roomUrl);
      if (displayName) u.searchParams.set("t", displayName);
      if (audioOnly) u.searchParams.set("startVideoOff", "true");
      return u.toString();
    } catch {
      return roomUrl;
    }
  })();

  const mm = String(Math.floor((remaining || 0) / 60)).padStart(2, "0");
  const ss = String((remaining || 0) % 60).padStart(2, "0");

  const wrapperCls = (() => {
    if (mode === "fullscreen") return "fixed inset-2 lg:inset-8 z-[120] bg-white border border-slate-200 rounded-sm shadow-2xl flex flex-col";
    if (mode === "minimized") return "fixed bottom-4 right-4 z-[120] w-[280px] bg-white border border-slate-200 rounded-sm shadow-2xl flex flex-col overflow-hidden";
    return "fixed top-4 right-4 z-[120] w-[560px] max-w-[calc(100vw-32px)] h-[680px] max-h-[calc(100vh-32px)] bg-white border border-slate-200 rounded-sm shadow-2xl flex flex-col";
  })();

  const copyLink = () => {
    if (!roomUrl) return;
    navigator.clipboard.writeText(roomUrl).then(() => toast.success("Call link copied"));
  };

  // Browser fullscreen — expands the whole panel to fill the physical screen.
  const wrapperRef = useRef(null);
  const goFullscreen = async () => {
    const el = wrapperRef.current;
    if (!el) return;
    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
      } else if (el.requestFullscreen) {
        await el.requestFullscreen();
      } else if (el.webkitRequestFullscreen) {
        el.webkitRequestFullscreen();
      }
      setMode("fullscreen");
    } catch { /* ignore: user gesture / permissions */ }
  };

  return (
    <div ref={wrapperRef} className={wrapperCls} data-testid="embed-call-panel">
      <div className="flex items-center gap-2 px-3 py-2.5 bg-kb-ink text-white shrink-0">
        <Phone className="w-3.5 h-3.5 text-emerald-300" />
        <div className="flex-1 min-w-0">
          <div className="text-xs font-medium leading-tight">
            {audioOnly ? "Audio" : "Video"} call · <span className="text-emerald-300">Daily.co (in-app)</span>
          </div>
          <div className="kb-mono text-[9px] uppercase tracking-wider text-white/60 truncate">
            {roomUrl ? roomUrl.split("/").slice(-1)[0] : "preparing room…"}
          </div>
        </div>
        <div className="kb-mono text-[10px] tabular-nums px-1.5 py-0.5 rounded-sm bg-white/10 inline-flex items-center gap-1">
          <Clock className="w-3 h-3" /> {mm}:{ss}
        </div>
        <button onClick={copyLink} className="p-1 text-white/70 hover:text-white" title="Copy invite link" data-testid="embed-call-copy">
          <Copy className="w-3.5 h-3.5" />
        </button>
        {mode !== "minimized" ? (
          <button onClick={() => setMode("minimized")} className="p-1 text-white/70 hover:text-white" title="Minimise" data-testid="embed-call-min">
            <Minimize2 className="w-3.5 h-3.5" />
          </button>
        ) : (
          <button onClick={() => setMode("default")} className="p-1 text-white/70 hover:text-white" title="Restore" data-testid="embed-call-restore">
            <Maximize2 className="w-3.5 h-3.5" />
          </button>
        )}
        {mode !== "fullscreen" && mode !== "minimized" && (
          <button onClick={goFullscreen} className="p-1 text-white/70 hover:text-white" title="Fullscreen (whole window)" data-testid="embed-call-max">
            <Maximize2 className="w-3.5 h-3.5" />
          </button>
        )}
        {mode === "fullscreen" && (
          <button onClick={async () => { try { if (document.fullscreenElement) await document.exitFullscreen(); } catch { /* ignore */ } setMode("default"); }} className="p-1 text-white/70 hover:text-white" title="Restore" data-testid="embed-call-restore-2">
            <Minimize2 className="w-3.5 h-3.5" />
          </button>
        )}
        <button onClick={onClose} className="p-1 text-white/70 hover:text-rose-300" title="End call" data-testid="embed-call-close">
          <X className="w-4 h-4" />
        </button>
      </div>
      {/* Keep the iframe mounted at all times — unmounting it drops the Daily
          call session (mic / camera / peer connection) and the auto-remount
          on restore forced the user to re-join. We toggle visibility via CSS
          in minimized mode instead. */}
      {!builtUrl ? (
        <div className="flex-1 flex items-center justify-center text-xs text-kb-ink-muted bg-slate-50">Preparing room…</div>
      ) : (
        <>
          {mode === "minimized" && (
            <button
              onClick={() => setMode("default")}
              className="px-3 py-3 text-xs text-kb-ink-muted hover:bg-slate-50 text-left"
              data-testid="embed-call-min-restore"
            >
              Call in progress — click to expand · {mm}:{ss}
            </button>
          )}
          {/* Plain iframe — Daily.co serves the full call UI from the room URL.
              `allow` is mandatory: without it the browser blocks camera / mic
              access inside the iframe (white blank screen with no permission
              prompt — the previous bug). */}
          <iframe
            src={builtUrl}
            title="Daily.co call"
            allow="camera; microphone; fullscreen; display-capture; autoplay; clipboard-write"
            className={mode === "minimized" ? "w-0 h-0 border-0 opacity-0 pointer-events-none absolute" : "flex-1 w-full border-0 bg-slate-100"}
            data-testid="daily-iframe"
          />
        </>
      )}
    </div>
  );
}
